import { fromEntries, objectEntries } from './collection';
import { is } from './helpers';

const consentGroups = {
	C0001: 'required',
	C0002: 'analytics',
	C0003: 'functional',
	C0004: 'marketing',
	C0005: 'social',
} as const;

type ConsentGroupId = keyof typeof consentGroups;
type ConsentGroupName = (typeof consentGroups)[ConsentGroupId];

/**
 * Consent groups for customer relations purposes (relevant for backend and
 * external systems).
 */
const CUSTOMER_RELATIONS_CONSENT_GROUP_NAMES = [
	'analytics',
	'marketing',
	'social',
] as const satisfies ConsentGroupName[];

interface ConsentGroup {
	hasConsented: boolean;
	id: ConsentGroupId;
	name: ConsentGroupName;
}
interface ConsentData {
	awaitingReconsent: boolean | undefined;
	consentId: string | undefined;
	datestamp: string | undefined;
	geolocation: string | undefined;
	groups: ConsentGroup[];
	hosts: string | undefined;
	interactionCount: number | undefined;
	isIABGlobal: boolean | undefined;
	landingPath: string | undefined;
	version: string | undefined;
}

/** Parse OneTrust consent cookie data. */
export function parseConsentCookie(
	cookieValue: string | undefined,
): ConsentData | undefined {
	if (!cookieValue) {
		return undefined;
	}

	const data: Record<string, string | undefined> = {};
	for (const param of cookieValue.split('&')) {
		const [key, value] = param.split('=');
		if (key && value) {
			data[key] = value;
		}
	}

	// Decoded format: 'C0001:1,C0002:0'
	const cookieGroups = data.groups
		? decodeURIComponent(data.groups)
				.split(',')
				.map((g) => g.split(':') as [string, `${0 | 1}`])
		: [];

	return {
		awaitingReconsent: data.AwaitingReconsent === 'true',
		consentId: data.consentId,
		datestamp: data.datestamp ? decodeURIComponent(data.datestamp) : undefined,
		geolocation: data.geolocation,
		groups: objectEntries(consentGroups).map(([id, name]) => ({
			id,
			name,
			hasConsented: cookieGroups.find((g) => g[0] === id)?.[1] === '1',
		})),
		hosts: data.hosts,
		interactionCount: data.interactionCount
			? Number.parseInt(data.interactionCount, 10)
			: undefined,
		isIABGlobal: data.isIABGlobal === 'true',
		landingPath: data.landingPath,
		version: data.version,
	};
}

/** Keys are group names, values whether the group has consent. */
export function getConsentGroupsByName(consentCookieValue: string | undefined) {
	const consentData = parseConsentCookie(consentCookieValue);
	return fromEntries(
		Object.values(consentGroups).map((groupName) => [
			groupName,
			consentData?.groups.find((g) => g.name === groupName)?.hasConsented ??
				false,
		]),
	) satisfies Record<ConsentGroupName, boolean>;
}

export function getCustomerRelationsConsentGroups(
	consentCookieValue: string | undefined,
) {
	return parseConsentCookie(consentCookieValue)?.groups.filter((g) =>
		is.oneOf(g.name, ...CUSTOMER_RELATIONS_CONSENT_GROUP_NAMES),
	);
}

export function getConsentHeader(
	consentCookieValue: string | undefined,
): { 'X-Consent': string } | undefined {
	const groupNames = getCustomerRelationsConsentGroups(consentCookieValue)
		?.filter((g) => g.hasConsented)
		.map((g) => g.name);
	if (is.arrayWithLength(groupNames)) {
		return { 'X-Consent': groupNames.join(', ') };
	}

	return undefined;
}
